import { AxiosError } from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';
import { UseFormGetValues, UseFormRegister, UseFormReset, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { CIVILITY_MADAME_CODE, CIVILITY_MONSIEUR_CODE, ERROR_MESSAGES, MAX_SMALL_MOBILE_WIDTH } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import { IApiErrors, IExtractedSecurityInfo, IUploadedDocument} from '../../context/model';
import MgpFormCheckbox from '../../elements/MgpFormCheckbox/MgpFormCheckbox';
import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';
import MgpMessage from '../../elements/MgpMessage/MgpMessage';
import UploadFilePreviewModal from '../../modals/common/UploadFilePreviewModal';
import { deleteDocument, downloadUploadedDocument, uploadSocialSecurity } from "../../services/CustomerApiService";
import { accessJsonAttr, calculNirKey, getAge, getCompressFile, getFilename, getValueNoSpace, isAcceptedFileSize, isValidFileSize, isValidFileType } from '../../utils/utils';
import QrCodeModal from '../../modals/common/QrCodeModal';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { displaySecuNumber } from '../../utils/dataUtils';
import ListOfFileUploaded from '../common/ListOfFileUploaded';
import UploadFromPhoneButton from '../common/UploadFromPhoneButton';
import MgpInputFileUpload from '../../elements/MgpInputFileUpload/MgpInputFileUpload';
import ImageModal from '../../modals/common/ImageModal';
import * as Yup from 'yup';
import LoaderModal from '../../modals/common/LoaderModal';
import moment from 'moment';

export type InfoAttestationROFormData = {
  attachmentOrganization: string;
  fileAttachId: string | null;
  alsaceMoselleRegime: boolean;
  canBeModified: boolean;
  nir: string;
  nirKey: string;
  civilityCode: string;
  birthDate: string;
  rank: string
}

type InfoAttestationROTemplateProps = {
    register: UseFormRegister<any>,
    setValue: UseFormSetValue<any>,
    getValues: UseFormGetValues<any>,
    reset: UseFormReset<any>,
    errors: any,
    canBeModified: boolean
}

export const validationSchemaInfoAttestationRO = Yup.object().shape({
    fileAttachId: Yup.string().required(ERROR_MESSAGES.requiredMsg),
    nir: Yup.string().when('canBeModified', {
        is: true,
        then: (schema) => schema
          .required(ERROR_MESSAGES.requiredMsg)
          .min(13, ERROR_MESSAGES.invalidMsg)
          .max(18, ERROR_MESSAGES.invalidMsg)
          .when('civilityCode', (civilityCode, schema) => {
            return schema.when('birthDate', (birthDate: any) => {
              if(getAge(birthDate) >= 16){
                return schema.test("follow_civility", "La valeur ne correspond pas à sa civilité", (val:string) => {
                  const valueNoSpace = getValueNoSpace(val);
                  return (civilityCode === CIVILITY_MONSIEUR_CODE && valueNoSpace?.startsWith("1")) || (civilityCode === CIVILITY_MADAME_CODE && valueNoSpace?.startsWith("2"));
                });
              }
            })
          })

          .when('birthDate', (birthDate, schema) => {
            if(birthDate && moment(birthDate).isValid()){
              if(getAge(birthDate) >= 16){          
                return schema.test("follow-birthDate", "La valeur ne correspond pas à sa date de naissance", (val:string) => { 
                  const valueNoSpace = getValueNoSpace(val);

                  const isProvisoire = valueNoSpace.startsWith("7") || val.startsWith("8");
                  if (isProvisoire) return true;

                  const fourthChar = valueNoSpace?.charAt(3);
                  if (fourthChar && Number(fourthChar) > 1) return true;

                  return valueNoSpace && birthDate.substring(5, 7) === valueNoSpace.substring(3,5) && birthDate.substring(2, 4) === valueNoSpace.substring(1,3); 
                }); 
              }
            }        
            return schema;
        }),
    }),
    nirKey: Yup.string().when('canBeModified', {
        is: true,
        then: (schema) => schema
        .required(ERROR_MESSAGES.requiredMsg)
        .min(2, ERROR_MESSAGES.invalidMsg)
        .max(2, ERROR_MESSAGES.invalidMsg)
        .when('nir', (nir, schema) => {
        return schema.test("nir-val", ERROR_MESSAGES.invalidMsg, (val: string) => {
          const valueNoSpace = getValueNoSpace(val);
          const nirNoSpace = getValueNoSpace(nir);
          return valueNoSpace && Number(valueNoSpace) && nirNoSpace && valueNoSpace === calculNirKey(nirNoSpace);
          }); 
        }),

    }),
    attachmentOrganization: Yup.string().required(ERROR_MESSAGES.requiredMsg)
    .min(9, ERROR_MESSAGES.invalidMsg)
    .max(11, ERROR_MESSAGES.invalidMsg)
    .matches(/^[0-9, ]+$/i, ERROR_MESSAGES.invalidMsg),
             
  });

export default function InfoAttestationROTemplate({ register, errors, setValue, canBeModified, reset, getValues} : Readonly<InfoAttestationROTemplateProps>) {

  const {
    state: { proposition, jwtToken, socialSecurityDocuments },
    dispatch,
  } = useAppState();
  
  const [errorMsg, setErrorMsg] = useState<string | null>();
  
  const [errorFileTypeMsg, setErrorFileTypeMsg] = useState<boolean | null>();
  const [errorFileSizeMsg, setErrorFileSizeMsg] = useState<boolean | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [openPreviewFile, setOpenPreviewFile] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [showUploadButton, setShowUploadButton] = useState<boolean>(false);
  const [isPrincipalDoc, setIsPrincipalDoc] = useState<boolean>(false); 
  const [imageUrl, setImageUrl] = useState<string>('');
  const [modalText, setModalText] = useState<string>('');

  const {width} = useWindowDimensions();
  const isDesktop = width > MAX_SMALL_MOBILE_WIDTH;
 


  const showQrCodePrincipal= () => {
    setIsPrincipalDoc(true)
    setIsShowModal(true);
  }

  const showQrCodeModal = () => {
    setIsShowModal(true);
  }
  const closeModal = () => {
    setIsShowModal(false)
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>, isPrincipalDoc: boolean) => {
    try {
      const uploadedfile = event.target.files ? event.target.files[0] : null;
  
      if (uploadedfile) {
        if(!isValidFileType(uploadedfile)) {
          setErrorFileTypeMsg(true)
        } else if(!isValidFileSize(uploadedfile)) {
          setErrorFileSizeMsg(true)
        } else if (!isAcceptedFileSize(uploadedfile)) {
          try {
            const token = jwtToken!.accessToken;
            const newFile = await getCompressFile(token, uploadedfile);
            setFile(newFile);
            setOpenPreviewFile(true);
            setShowUploadButton(true);
            setIsPrincipalDoc(isPrincipalDoc);
            dispatch({type: 'SHOW_MODAL', showModal: true});   
          } catch(e) {
            setErrorMsg("Suite à un incident technique, nous ne pouvons pas uploader votre fichier. Veuillez sélectionner à nouveau.");
          }    
        } else {
          setFile(uploadedfile);
          setOpenPreviewFile(true);
          setShowUploadButton(true);
          setIsPrincipalDoc(isPrincipalDoc);
          dispatch({type: 'SHOW_MODAL', showModal: true});
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  };

  const onUploadAction = async () => {
    setErrorMsg(null);
    setErrorFileTypeMsg(false);
    setErrorFileSizeMsg(false);
    setLoading(true);
    try {
      const token = jwtToken!.accessToken;
      const res = await uploadSocialSecurity(token, proposition!.propositionId, file as File, true, isPrincipalDoc);
      
      setLoading(false);
      if (res.data) {
        onUpdatedFormValue(res.data);
      } else {
        setErrorMsg(ERROR_MESSAGES.lowQualityDoc);
      }
   
    } catch (error) {
      setLoading(false);
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue.");
      }
    }
  }
  const onEventMessage = (message: any, type: string) => {
    if(type === 'upload') {
      const socialSecurityDocuments = message as IUploadedDocument<IExtractedSecurityInfo>;
      onUpdatedFormValue(socialSecurityDocuments);
      setIsShowModal(false);
      setShowUploadButton(false);
    }
  }

  const onUpdatedFormValue = (secuDocument: IUploadedDocument<IExtractedSecurityInfo>) => {
    reset({
      ...getValues(),
      nir: '',
      nirKey: '',
      fileAttachId: secuDocument.id,
      attachmentOrganization: ''
    })
    const principalBeneficiary = secuDocument.extractedData.beneficiaries.find(user => user.isPrincipalBeneficiary);
    if(secuDocument && 
      secuDocument.extractedData.isValid && 
      secuDocument.extractedData.isAttestationSecu &&
      secuDocument.extractedData.isValidDuration && 
      principalBeneficiary && 
      principalBeneficiary.isValidSecurityNumber &&
      principalBeneficiary.isValidNir &&
      principalBeneficiary.isValidSocialSecurityOrg){  

        setValue('fileAttachId', secuDocument.id);
        setValue('nir',principalBeneficiary.nir )
        setValue('nirKey',principalBeneficiary.nirKey )
        setValue(
          'attachmentOrganization', 
          principalBeneficiary.socialSecurityOrg.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3').trim(), 
          {shouldValidate: true}
        )
        setValue("rank", principalBeneficiary.rank ?? "1");  
      } else {
        reset({
          ...getValues(),
          nir: '',
          nirKey: '',
          fileAttachId: secuDocument.id,
          attachmentOrganization: '',
          rank: ''
        })
      }
      dispatch({type: 'SHOW_MODAL', showModal: false});   
      dispatch({
        type: 'EXTRACT_SECU_ATTEST', 
        socialSecurityDocuments: [...socialSecurityDocuments, secuDocument]
      });
  }

  const onDownLoadDocument = async (documentId: string) => {
    try {
      const token = jwtToken!.accessToken;
      const res = await downloadUploadedDocument(token, proposition!.propositionId, documentId);
      const content = new Blob([res.data], {type: res.headers['content-type']});
      const filename = getFilename(res.headers);
      const file = new File([content], filename);
      setFile(file);
      setOpenPreviewFile(true);
      setShowUploadButton(false);
      dispatch({type: 'SHOW_MODAL', showModal: true});

    } catch (error) {
      setLoading(false);

      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else if (err.response && err.response.status === 404) {
        setErrorMsg(ERROR_MESSAGES.documentNotFound);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  }

  const onDeleteDocLocal = (documentId: string) => {
    const newDocumentArray = [...socialSecurityDocuments];
    const index= newDocumentArray.findIndex(el => el.id === documentId);
    newDocumentArray.splice(index, 1);
    dispatch({
      type: 'EXTRACT_SECU_ATTEST', 
      socialSecurityDocuments: newDocumentArray
    });
    reset({
        ...getValues(),
        nir: '',
        nirKey: '',
        fileAttachId: null,
        attachmentOrganization: '',
        rank: ''
    })
    setErrorMsg(null);
  }

  const onDeleteDocument = async (documentId: string) => {
    setLoading(true);
    try {
      const token = jwtToken!.accessToken;
      await deleteDocument(token, proposition!.propositionId, documentId);

      onDeleteDocLocal(documentId);
      setLoading(false);
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else if (err.response && err.response.status === 404) {
        onDeleteDocLocal(documentId);
        setErrorMsg(null);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
      setLoading(false);
    }
    
  }

  const handleOpenInfoModal = (e: any, image: string, text?: string) => {
    e.preventDefault();
    setImageUrl(image);
    if (text) setModalText(text);
  }
  
  const handleCloseInfoModal = () => {
    setImageUrl('');
    setModalText('');
  }

  const displayPrincipalBeneficiary = () => {
    let attachmentOrganization = proposition?.contact.attachmentOrganization;
    let message = '';
    let status = 'info';
    const attestationPrincipal = socialSecurityDocuments.find(doc => doc.name === "Attestation de droits");
    if(attestationPrincipal && 
      !attestationPrincipal.extractedData?.isAttestationSecu ) {
        message = ERROR_MESSAGES.invalidRODoc;
        status = 'alert';

    } else if(attestationPrincipal && 
      attestationPrincipal.extractedData?.isValidDuration === false) {
        message = ERROR_MESSAGES.invalidDateRODoc;
        status = 'alert';

    } else if 
      (attestationPrincipal?.extractedData && 
      attestationPrincipal.extractedData.beneficiaries?.length > 0) {
        const principalBeneficiary = attestationPrincipal.extractedData.beneficiaries.find(user => user.isPrincipalBeneficiary);
   
        if (principalBeneficiary && 
          (!principalBeneficiary?.isValidSecurityNumber || !principalBeneficiary?.isValidNir)) {
            message = ERROR_MESSAGES.invalidNIR
            status = 'alert';

        } else if(!principalBeneficiary) {
          message = ERROR_MESSAGES.invalidRONom;
          status = 'alert';

        } else if
            ( principalBeneficiary && 
              attachmentOrganization && 
              attachmentOrganization !== principalBeneficiary.socialSecurityOrg) {
                message = ERROR_MESSAGES.invalidROOrganisation;
                status = 'alert';
        } else  {
            attachmentOrganization = principalBeneficiary.socialSecurityOrg;
            message = 'Veuillez vérifier que votre organisme de rattachement est correct. N’hésitez pas à le corriger si besoin.';
            status = 'info';
          }
      }
   
    return (<div>
      {message &&
        <p className={'text bg-menthe blanc m-y-md p-sm mw-100 ' + (status === 'alert' ? 'bg-orange' : 'bg-menthe')}>
          {message}
        </p>
      }
      <div className='form-row'>
        {attestationPrincipal ?
          <div className="form-row">
            <div className='form-group mw-100'>
              <ListOfFileUploaded 
                fileName={attestationPrincipal.name}
                onDownloadDocument={() => onDownLoadDocument(attestationPrincipal.id)}
                onDeleteDocument={() => onDeleteDocument(attestationPrincipal.id)}
              />
            </div>
          </div>
          :
          <div className='form-group'>
            <div className="form-row">
              <div className='form-group mw-45 m-y-md'>
                <MgpInputFileUpload
                id="fileAttachId"
                onChange={(e) => handleFileChange(e, true)}
                register={register} 
                errors={errors}
                required={true}
                onClick={(e)=> e.currentTarget.value = ''}
                />
              </div>
              {
                isDesktop && <div className="form-group mw-5 ta-center" style={{paddingTop: "100px"}}><p className='fz-lg'>ou</p></div>
              }
              {isDesktop && 
              <div className='form-group mw-45'>
                <UploadFromPhoneButton onClick={() => showQrCodePrincipal()}/> 
              </div>}
            </div>
            {errors && accessJsonAttr(errors, 'fileAttachId') && <p className="error mt-sm fushia">(*) Votre attestation est obligatoire pour passer à la prochaine étape</p>} 
          </div>
        }
      </div>
    </div>);
  }

  const displaySocialSecurityInfo = () => {
    return (<div>
      <div className=''>
        <div className='form-row'>
          
            {
              canBeModified ?
                <div className='form-row'>
                  <div className="form-group mw-80">
                    <MgpFormInput
                      label="Numéro de sécurité sociale"
                      id='nir'
                      register={register}
                      errors={errors}
                      labelFontWeight='regular'
                      required={true}
                      maxLength={13}
                      regex={[/(\d{1})(\d{2})(\d{2})(\d{2})(\d{3})(\d{3})/, '$1 $2 $3 $4 $5 $6']}
                      tooltipText="Cliquez pour voir les instructions."
                      handleClickInfo={(e: any) => handleOpenInfoModal(e, "Image_AM_CV_entourage_3.jpg", "Il s'agit des 13 premiers numéros présents sur l'attestation de droits ou la carte vitale.")}
                    />
                  </div>
                  <div className="form-group mw-20">
                  
                    <MgpFormInput
                      label="Clé"
                      id={`nirKey`}
                      register={register}
                      errors={errors}
                      labelFontWeight='regular'
                      required={true}
                      maxLength={2}
                      tooltipText="Cliquez pour voir les instructions."
                      handleClickInfo={(e: any) => handleOpenInfoModal(e, "Image_AM_CV_entourage_2.jpg", "Il s'agit des deux derniers chiffres de votre numéro de sécurité sociale, composé de 15 chiffres.")}
                    />
                  </div>
                </div>
                :
                <div className="form-group mw-100">
                    <MgpFormInput
                      label="Numéro de sécurité sociale"
                      id="contact.nir"
                      labelFontWeight='regular'
                      value={displaySecuNumber(proposition?.contact.nir, proposition?.contact.nirKey)}
                      disabled={true}
                    />
                  </div>
            }
                
         
        </div>
       
          <div className="form-row">
            <div className="form-group mw-100">
              <MgpFormInput
                label="Organisme de rattachement"
                id="attachmentOrganization"
                labelFontWeight='regular'
                register={register}
                errors={errors}
                required={true}
                maxLength={9}
                regex={[/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3']}
                handleClickInfo={(e: any) => handleOpenInfoModal(e, 'Image_AM_1.jpg', "L'organisme de rattachement est composé de 9 chiffres.")}
                tooltipText="Cliquez pour voir les instructions"
              />
            </div>
          </div>
      </div>  
     
      <div className="pb-xl pt-sm form-row">
        <div className="form-group form-check mw-100">
          <MgpFormCheckbox id='alsaceMoselleRegime' label="Je relève du régime Alsace-Moselle" register={register} errors={errors} />
        </div> 
      </div>
    </div>)
  }
  

  return (         
      <form style={{width: '100%'}}> 
        <div className='form-row page-content'>
          <div className='col-flex-4 bg-blanc shadow-noir p-md m-y-xs'>
            <h2 className="card-title bleu step-title">Etape 1 : Je joins mon attestation de droits à l'assurance maladie</h2>
            <p className='text mt-xs' style={{textAlign: 'justify'}}>Pour faciliter la saisie automatique de vos informations, vous devez télécharger votre attestation lisible et à jour.</p>
            
            <div className='form-row'>
              <div className='form-group mw-100'>
                {errorFileTypeMsg && <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileTypeMsg}</p> }
                {errorFileSizeMsg && <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileSizeMsg}</p> }
              </div>
            </div> 
            
            {displayPrincipalBeneficiary()}
            <div>
              {errorMsg && <MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/>}
            </div>


          </div>

          <div className='col-flex-4 bg-blanc shadow-noir p-md m-y-xs'>
            <h2 className="card-title bleu step-title">Etape 2 : Je vérifie ou complète mes informations</h2>
            <div className=''>
              {displaySocialSecurityInfo()}
            </div>

            <div className="mt-xl">
              <p className='text fw-medium'><span className='fushia'>(*)</span> Champs obligatoires</p>
            </div>
          </div>
        </div>

        {openPreviewFile&&file&&<UploadFilePreviewModal file={file} onUploadAction={onUploadAction} isOpen={openPreviewFile} onCloseModal={() => {setOpenPreviewFile(false); dispatch({type: 'SHOW_MODAL', showModal: false});}} showButton={showUploadButton}/>} 

        {imageUrl && <ImageModal isOpen={true} name={imageUrl} onCloseModal={handleCloseInfoModal} text={modalText}/>}

        {isShowModal && <QrCodeModal isOpen={isShowModal} onShowQrCode={showQrCodeModal} onCloseModal={closeModal} docCode={isPrincipalDoc ? "SOCIAL_SECURITY_CERTIFICATE" : "SOCIAL_SECURITY_CERTIFICATE_BENEFICIARY"} onEventMessage={onEventMessage} /> }
         
        {loading && <LoaderModal hasMessage={true}/>}
      </form>
  )
}
